/**
 * useViewTransition Hook
 * 2026 CSS-Native Standard - View Transitions API
 *
 * Provides smooth, native page transitions using the View Transitions API.
 * Falls back gracefully for browsers without support.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
 */

import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

// Type declarations for View Transitions API (not yet in all TypeScript libs)
declare global {
  interface Document {
    startViewTransition?: (callback: () => void | Promise<void>) => ViewTransition;
  }

  interface ViewTransition {
    finished: Promise<void>;
    ready: Promise<void>;
    updateCallbackDone: Promise<void>;
    skipTransition: () => void;
  }
}

interface UseViewTransitionOptions {
  /** Duration of the transition in milliseconds */
  duration?: number;
  /** Whether to skip transition when user prefers reduced motion */
  respectReducedMotion?: boolean;
  /** Callback when transition starts */
  onTransitionStart?: () => void;
  /** Callback when transition completes */
  onTransitionEnd?: () => void;
}

interface UseViewTransitionReturn {
  /** Whether View Transitions API is supported */
  isSupported: boolean;
  /** Whether a transition is currently in progress */
  isTransitioning: boolean;
  /** Start a view transition with the given callback */
  startTransition: (callback: () => void | Promise<void>) => Promise<void>;
  /** Start a view transition that wraps a state update */
  transitionTo: <T>(setter: (value: T) => void, value: T) => Promise<void>;
}

/**
 * Hook for using the View Transitions API with React state updates
 *
 * @example
 * ```tsx
 * const { startTransition, transitionTo, isSupported } = useViewTransition();
 *
 * // Option 1: Wrap any callback
 * const handleNavigate = (page: Page) => {
 *   startTransition(() => setActivePage(page));
 * };
 *
 * // Option 2: Direct state transition
 * const handleNavigate = (page: Page) => {
 *   transitionTo(setActivePage, page);
 * };
 * ```
 */
export function useViewTransition(options: UseViewTransitionOptions = {}): UseViewTransitionReturn {
  const {
    respectReducedMotion = true,
    onTransitionStart,
    onTransitionEnd,
  } = options;

  const isTransitioningRef = useRef(false);

  // Check for API support
  const isSupported = typeof document !== 'undefined' &&
    typeof document.startViewTransition === 'function';

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const shouldSkipTransition = respectReducedMotion && prefersReducedMotion;

  const startTransition = useCallback(async (callback: () => void | Promise<void>) => {
    // If API not supported or should skip, just run the callback
    if (!isSupported || shouldSkipTransition) {
      await callback();
      return;
    }

    // Prevent overlapping transitions
    if (isTransitioningRef.current) {
      await callback();
      return;
    }

    isTransitioningRef.current = true;
    onTransitionStart?.();

    try {
      // Use flushSync to force React to update the DOM synchronously
      // This is required because View Transitions API captures "old" and "new" views,
      // but React's async updates would cause it to capture the same view twice
      const transition = document.startViewTransition!(() => {
        flushSync(() => {
          callback();
        });
      });

      await transition.finished;
    } catch (error) {
      // If transition fails, ensure callback still runs
      console.warn('[ViewTransition] Transition failed, running callback directly:', error);
      await callback();
    } finally {
      isTransitioningRef.current = false;
      onTransitionEnd?.();
    }
  }, [isSupported, shouldSkipTransition, onTransitionStart, onTransitionEnd]);

  const transitionTo = useCallback(async <T>(
    setter: (value: T) => void,
    value: T
  ) => {
    await startTransition(() => {
      setter(value);
    });
  }, [startTransition]);

  return {
    isSupported,
    isTransitioning: isTransitioningRef.current,
    startTransition,
    transitionTo,
  };
}

/**
 * Utility to add view-transition-name to an element
 * Use this for elements that should animate independently during transitions
 *
 * @example
 * ```tsx
 * <header style={viewTransitionName('header')}>...</header>
 * <main style={viewTransitionName('main-content')}>...</main>
 * ```
 */
export function viewTransitionName(name: string): React.CSSProperties {
  return {
    viewTransitionName: name,
  } as React.CSSProperties;
}

/**
 * CSS class helper for view transition elements
 */
export const viewTransitionClasses = {
  fade: 'view-transition-fade',
  slide: 'view-transition-slide',
  scale: 'view-transition-scale',
  hero: 'view-transition-hero',
} as const;

export default useViewTransition;
